'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { MapPinIcon, PencilIcon, PlusIcon, StarIcon, TrashIcon } from 'lucide-react'
import { toast } from 'sonner'
import { DeleteConfirmationDialog } from '@/components/partials'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { DataTablePagination } from '@/components/ui/data-table-pagination'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AddressFormDialog } from './address-form-dialog'
import { useCreateAddress, useDeleteAddress, useSetPrimaryAddress, useUpdateAddress, useUserAddresses } from '@/hooks'
import { FormModeEnum } from '@/enums'
import { hasPermission } from '@/lib/permissions'
import { formatDateTime, getErrorMessage } from '@/lib/utils'
import { useMeStore } from '@/store'
import { AddressType, CreateAddressParams, UpdateAddressParams } from '@/types/address'

type PropsType = {
  userId: string
}

const UserAddresses = ({ userId }: PropsType) => {
  const t = useTranslations()
  const locale = useLocale()
  const queryClient = useQueryClient()
  const { meState } = useMeStore()
  const currentUser = meState.response

  const canView = hasPermission(currentUser, 'ADDRESSES', 'LIST') || hasPermission(currentUser, 'ADDRESSES', 'SHOW')
  const canCreate = hasPermission(currentUser, 'ADDRESSES', 'CREATE')
  const canUpdate = hasPermission(currentUser, 'ADDRESSES', 'UPDATE')
  const canDelete = hasPermission(currentUser, 'ADDRESSES', 'DELETE')

  const [page, setPage] = useState(1)
  const [size, setSize] = useState(10)
  const [createOpen, setCreateOpen] = useState(false)
  const [editAddress, setEditAddress] = useState<AddressType | null>(null)
  const [deleteAddressItem, setDeleteAddressItem] = useState<AddressType | null>(null)

  const params = useMemo(
    () => ({
      page,
      size,
      sortBy: 'createdAt' as const,
      sort: 'desc'
    }),
    [page, size]
  )

  const { data, isLoading, error } = useUserAddresses(userId, params, { enabled: canView })
  const { mutate: createAddress, isPending: isCreating } = useCreateAddress()
  const { mutate: updateAddress, isPending: isUpdating } = useUpdateAddress()
  const { mutate: deleteAddress, isPending: isDeleting } = useDeleteAddress()
  const { mutate: setPrimaryAddress, isPending: isSettingPrimary } = useSetPrimaryAddress()

  if (!canView) {
    return null
  }

  const addresses = data?.items || []
  const isMutating = isCreating || isUpdating || isDeleting || isSettingPrimary

  const invalidateAddressQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['user-addresses', userId] }),
      queryClient.invalidateQueries({ queryKey: ['user-address', userId] })
    ])
  }

  const handleCreate = (formData: CreateAddressParams | UpdateAddressParams) => {
    createAddress(
      { userId, params: formData as CreateAddressParams },
      {
        onSuccess: async () => {
          toast.success(t('address_created'))
          setCreateOpen(false)
          setPage(1)
          await invalidateAddressQueries()
        },
        onError: error => {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
        }
      }
    )
  }

  const handleUpdate = (formData: CreateAddressParams | UpdateAddressParams) => {
    if (!editAddress) {
      return
    }

    updateAddress(
      { userId, addressId: editAddress.id, params: formData as UpdateAddressParams },
      {
        onSuccess: async () => {
          toast.success(t('address_updated'))
          setEditAddress(null)
          await invalidateAddressQueries()
        },
        onError: error => {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
        }
      }
    )
  }

  const handleDelete = () => {
    if (!deleteAddressItem) {
      return
    }

    deleteAddress(
      { userId, addressId: deleteAddressItem.id },
      {
        onSuccess: async () => {
          if (addresses.length === 1 && page > 1) {
            setPage(previousPage => previousPage - 1)
          }
          toast.success(t('address_deleted'))
          setDeleteAddressItem(null)
          await invalidateAddressQueries()
        },
        onError: error => {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
        }
      }
    )
  }

  const handleSetPrimary = (addressId: string) => {
    setPrimaryAddress(
      { userId, addressId },
      {
        onSuccess: async () => {
          toast.success(t('address_set_primary'))
          await invalidateAddressQueries()
        },
        onError: error => {
          toast.error(getErrorMessage(error, t('something_went_wrong')))
        }
      }
    )
  }

  const getLocationLabel = (address: AddressType) => {
    const values = [address.country?.name, address.state?.name, address.city?.name].filter(Boolean)
    return values.length > 0 ? values.join(' / ') : '-'
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle className="flex items-center gap-2">
            <MapPinIcon className="h-5 w-5" />
            {t('addresses')}
          </CardTitle>
          {canCreate && (
            <Button onClick={() => setCreateOpen(true)} disabled={isMutating}>
              <PlusIcon className="h-4 w-4" />
              <span className="hidden items-center gap-2 lg:flex">{t('add_address')}</span>
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-hidden rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('title')}</TableHead>
                  <TableHead>{t('location')}</TableHead>
                  <TableHead>{t('zip_code')}</TableHead>
                  <TableHead>{t('primary_address')}</TableHead>
                  <TableHead>{t('created_at')}</TableHead>
                  <TableHead className="text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                      {t('loading')}
                    </TableCell>
                  </TableRow>
                ) : error ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-destructive py-8 text-center">
                      {getErrorMessage(error, t('failed_to_load'))}
                    </TableCell>
                  </TableRow>
                ) : addresses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-muted-foreground py-8 text-center">
                      {t('no_addresses')}
                    </TableCell>
                  </TableRow>
                ) : (
                  addresses.map(address => (
                    <TableRow key={address.id}>
                      <TableCell className="font-medium">{address.title}</TableCell>
                      <TableCell>{getLocationLabel(address)}</TableCell>
                      <TableCell>{address.zipCode || '-'}</TableCell>
                      <TableCell>
                        {address.primary ? (
                          <Badge variant="success">
                            <StarIcon className="h-3 w-3 fill-current" />
                            {t('primary_address')}
                          </Badge>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>{formatDateTime(address.createdAt, locale)}</TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          {canUpdate && !address.primary && (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleSetPrimary(address.id)}
                              disabled={isMutating}>
                              <StarIcon className="h-4 w-4" />
                              <span className="hidden items-center gap-2 lg:flex">{t('set_as_primary')}</span>
                            </Button>
                          )}
                          {canUpdate && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditAddress(address)}
                              disabled={isMutating}>
                              <PencilIcon className="h-4 w-4" />
                              <span className="hidden items-center gap-2 lg:flex">{t('edit')}</span>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => setDeleteAddressItem(address)}
                              disabled={isMutating}>
                              <TrashIcon className="h-4 w-4" />
                              <span className="hidden items-center gap-2 lg:flex">{t('delete')}</span>
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <DataTablePagination
            currentPage={data?.page || page}
            totalPages={data?.pages || 1}
            totalItems={data?.total || 0}
            pageSize={data?.size || size}
            onPageChangeAction={setPage}
            onPageSizeChangeAction={nextSize => {
              setSize(nextSize)
              setPage(1)
            }}
          />
        </CardContent>
      </Card>

      <AddressFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode={FormModeEnum.CREATE}
        isLoading={isCreating}
        onSubmit={handleCreate}
      />

      <AddressFormDialog
        open={!!editAddress}
        onOpenChange={open => {
          if (!open) {
            setEditAddress(null)
          }
        }}
        mode={FormModeEnum.UPDATE}
        initialData={editAddress}
        isLoading={isUpdating}
        onSubmit={handleUpdate}
      />

      <DeleteConfirmationDialog
        open={!!deleteAddressItem}
        onCancel={() => setDeleteAddressItem(null)}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  )
}

export default UserAddresses
